/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useState,
  FocusEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { Box, IconButton } from "@material-ui/core"
import {
  fetchAgencyClientProfile,
  fetchClientConfigProfile,
  updateClientAgencyProfile,
} from "api/clients"
import { useMutation, useQuery } from "react-query"

import { Address } from "./Address"
import Button from "ui/buttons/Button"
import { ClientProfile } from "models/clientAgencyConf/profile"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import { Loading } from "components/Loading"
import { Phone } from "./Phone"
import { Select } from "components/inputs/Select"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import { agencyClientStyles } from "./styles"
import { css } from "@emotion/css"
import { get } from "lodash/fp"
import set from "lodash/fp/set"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useAgencyId } from "hooks/useAgencyId"

export function AgencyClientProfile() {
  const { id, configId } = useParams<{ id: string; configId: string }>()
  const { t } = useI18n()
  const sendFeedback = useFeedback((s) => s.send)
  const [profile, setProfile] = useState<Partial<ClientProfile>>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const { hasAuthority, meta } = useAuthenticated()
  const isTeamLead = meta?.teamLead
  const { agencyId } = useAgencyId()

  const hasBillingAddress = !!profile?.billingAddress

  const weekDayOptions = [
    "",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ].map((wd) => ({ label: t(wd), value: wd }))

  const budgetPlanningOptions = ["GROSS_COST", "NET_COST", "BILLABLE"].map(
    (bp) => ({ label: t(`agency.client.budgetPlanning.${bp}`), value: bp }),
  )

  const queryFn = isTeamLead
    ? fetchClientConfigProfile
    : fetchAgencyClientProfile
  const queryId = isTeamLead ? configId : id
  const enabled = isTeamLead ? queryId : agencyId

  const { data, isLoading, updatedAt } = useQuery(
    ["agency-client", queryId, agencyId],
    queryFn,
    {
      enabled,
      onError: (e) => sendFeedback(t(e.message), "error"),
    },
  )

  const [patch] = useMutation(updateClientAgencyProfile, {
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const patchPart = useCallback(
    (part: Partial<ClientProfile>) => {
      !!agencyId && patch({ agencyId, clientId: id, ...part })
    },
    [id, agencyId, patch],
  )

  const isDiff = useCallback(
    (name: string, newValue: string) => {
      const old = get(name, data)
      return old !== newValue
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatedAt],
  )

  const onAddPhone = useCallback(() => {
    setProfile((pr) =>
      set(
        "contact.phones",
        [
          ...(pr?.contact?.phones || []),
          { extension: "", number: "", type: "Business" },
        ],
        pr || {},
      ),
    )
  }, [])

  const profilePhones = profile?.contact?.phones
  const dataPhones = data?.contact?.phones

  const handleDeletePhone = useCallback(
    (idx: number) => {
      const phones =
        profile?.contact?.phones?.filter((_, id) => id !== idx) || []
      setProfile((pr) => {
        const updated = set("contact.phones", phones || [], pr || {})
        const { contact } = updated
        profilePhones?.length === dataPhones?.length && patchPart({ contact })
        return updated
      })
    },
    [
      dataPhones?.length,
      patchPart,
      profile?.contact?.phones,
      profilePhones?.length,
    ],
  )

  const onCopyBillingAddressToContact = useCallback(() => {
    if (hasBillingAddress) {
      setProfile((pr) => {
        const updated = set(
          "contact.address",
          pr?.billingAddress || {},
          pr || {},
        )
        const { contact } = updated
        patchPart({ contact })
        return updated
      })
    }
  }, [patchPart, hasBillingAddress])

  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    const { name, value } = evt?.target
    !!name && setErrors((e) => set(name, undefined, e || {}))
    !!name && setProfile((s) => set(name, value, s || {}))
  }

  const handleBlur = useCallback(
    (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name = "", value = "" } = evt?.target ?? {}
      if (isDiff(name, value)) {
        const patched = set(name, value, {})
        patchPart(patched)
      }
    },
    [patchPart, isDiff],
  )

  const handleContactBlur = useCallback(
    (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name = "", value = "" } = evt?.target ?? {}
      if (isDiff(name, value)) {
        const contact = profile?.contact
        patchPart({ contact })
      }
    },
    [profile?.contact, patchPart, isDiff],
  )

  useEffect(() => {
    !!data && setProfile(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  return isLoading ? (
    <Loading />
  ) : (
    <Box display="grid" gridAutoFlow="row" padding="var(--spacing-xxl-2) 0">
      <h5 className="BodyBody-1BlackBold">
        {t("vendors.profile.section.details")}
      </h5>
      <Box
        display="grid"
        gridAutoFlow="column"
        gridAutoColumns="max-content"
        gridColumnGap="1rem"
        alignItems="center"
        padding="0"
        marginTop="var(--spacing-xs)"
      >
        <TextInput
          name="localName"
          value={data?.localName || ""}
          label={t("agency.client.localName")}
          margin="normal"
          size="medium"
          disabled={true}
          className={agencyClientStyles.fieldWidth}
        />
        <TextInput
          name="groupName"
          value={data?.groupName || ""}
          label={t("agency.client.client-groups")}
          margin="normal"
          size="medium"
          className={agencyClientStyles.fieldWidth}
          disabled={true}
        />
      </Box>
      <Box
        display="grid"
        gridAutoFlow="column"
        gridAutoColumns="max-content"
        gridColumnGap="1rem"
        alignItems="center"
        padding="0"
        marginBottom="var(--spacing-xxl-2)"
      >
        <TextInput
          name="marketName"
          value={data?.marketName || ""}
          label={t("agency.client.market")}
          margin="normal"
          size="medium"
          className={agencyClientStyles.fieldWidth}
          disabled={true}
        />
        <TextInput
          name="subregionName"
          value={data?.subRegionName || ""}
          label={t("agency.client.subregion")}
          margin="normal"
          size="medium"
          className={agencyClientStyles.fieldWidth}
          disabled={true}
        />
        <TextInput
          name="regionName"
          value={data?.regionName || ""}
          label={t("agency.client.region")}
          margin="normal"
          size="medium"
          className={agencyClientStyles.fieldWidth}
          disabled={true}
        />
      </Box>

      {profile?.companies?.length ? (
        <Box
          display="grid"
          gridAutoFlow="row"
          padding="0 0 var(--spacing-xxl-2)"
        >
          <h5 className="BodyBody-1BlackBold">
            {t("vendors.profile.section.companies")}
          </h5>
          {profile?.companies?.map((c) => (
            <Fragment key={`companies.${c.id}`}>
              <Box
                display="grid"
                gridAutoFlow="column"
                gridAutoColumns="max-content"
                gridColumnGap="1rem"
                alignItems="center"
                padding="0"
                marginTop="var(--spacing-xs)"
              >
                <TextInput
                  name="company"
                  value={c?.name || ""}
                  label={t(`agency.client.company.name`)}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.fieldWidth}
                  disabled={true}
                />
                <TextInput
                  name="code"
                  value={c?.clientCode || ""}
                  label={t(`agency.client.company.code`)}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.fieldWidth}
                  disabled={true}
                />
              </Box>
            </Fragment>
          ))}
        </Box>
      ) : null}

      <Box>
        <h5 className="BodyBody-1BlackBold">{t("agency.client.addresses")}</h5>
        <Box
          display="grid"
          gridAutoFlow="column"
          gridAutoColumns="max-content"
          gridColumnGap="140px"
          padding="var(--spacing-xl) 0"
          marginBottom="var(--spacing-xxl-3)"
        >
          <Address
            address="businessAddress"
            source={profile?.businessAddress}
            updateSource={setProfile}
            disabled={true}
            patchPart={patchPart}
            handleBlur={handleBlur}
            handleChange={handleChange}
            errors={errors}
          />
        </Box>
      </Box>
      <Box>
        <h5 className="BodyBody-1BlackBold">{t("agency.billing.contact")}</h5>
        <Box
          padding="1rem 0"
          display="grid"
          gridAutoColumns="max-content"
          gridRowGap="var(--spacing-xl)"
        >
          <h5 className="BodyBody-1BlackRegular">{t(`agency.contact.info`)}</h5>
          <Box>
            <TextInput
              id="contact.firstName"
              name="contact.firstName"
              value={get(`contact.firstName`, profile) || ""}
              label={t("contact.title")}
              margin="none"
              size="medium"
              className={agencyClientStyles.titleInput}
              onChange={handleChange}
              onBlur={handleContactBlur}
              error={!!get(`contact.firstName`, errors)}
              errorMessage={get(`contact.firstName`, errors)}
              disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
            />
            <TextInput
              id="contact.lastName"
              name="contact.lastName"
              value={get(`contact.lastName`, profile) || ""}
              label={t("contact.name")}
              margin="none"
              size="medium"
              className={agencyClientStyles.nameInput}
              onChange={handleChange}
              onBlur={handleContactBlur}
              error={!!get(`contact.lastName`, errors)}
              errorMessage={get(`contact.lastName`, errors)}
              disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
            />
          </Box>
          <TextInput
            id="contact.email"
            name="contact.email"
            value={get(`contact.email`, profile) || ""}
            label={t("contact.email")}
            margin="none"
            size="medium"
            className={agencyClientStyles.mailInput}
            onChange={handleChange}
            onBlur={handleContactBlur}
            error={!!get(`contact.email`, errors)}
            errorMessage={get(`contact.email`, errors)}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
          />
          {profile?.contact?.phones?.map((phone, idx) => (
            <div className={agencyClientStyles.inputsWrapper}>
              <Phone
                idx={idx}
                key={`contact.phone.${idx}`}
                source={phone}
                onChange={handleChange}
                onBlur={handleContactBlur}
                errors={errors}
              />
              {idx > 0 ? (
                <WithAuthority allowed={["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]}>
                  <IconButton
                    className={agencyClientStyles.iconButton}
                    onClick={() => handleDeletePhone(idx)}
                  >
                    <DeleteIcon
                      color="inherit"
                      className={agencyClientStyles.deleteIcon}
                    />
                  </IconButton>
                </WithAuthority>
              ) : null}
            </div>
          ))}
          {hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"]) && (
            <Button
              className={agencyClientStyles.textButton}
              variant="default"
              color="primary"
              onClick={onAddPhone}
            >
              {t("add.phone")}
            </Button>
          )}
        </Box>

        <Box padding="1rem 0" marginBottom="var(--spacing-xxl-6)">
          <Address
            address="contact.address"
            source={profile?.contact?.address}
            updateSource={setProfile}
            patchPart={patchPart}
            handleBlur={handleContactBlur}
            handleChange={handleChange}
            disabled={true}
            errors={errors}
            onCopyFrom={
              hasBillingAddress ? onCopyBillingAddressToContact : undefined
            }
            copyTitle={t("copy.billing.address")}
          />
        </Box>
      </Box>
      <Box>
        <h5 className="BodyBody-1BlackBold">
          {t("agency.client.general.info")}
        </h5>
        <Box
          padding="1rem 0"
          display="grid"
          gridAutoColumns="max-content"
          gridRowGap="var(--spacing-xl)"
          marginTop="var(--spacing-m)"
        >
          <TextInput
            id="fiscalYear"
            name="fiscalYear"
            value={profile?.fiscalYear || ""}
            label={t("agency.client.fiscal.year")}
            margin="none"
            size="medium"
            className={agencyClientStyles.fieldWidth}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!get(`fiscalYear`, errors)}
            errorMessage={get(`fiscalYear`, errors)}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
          />
          <Select
            name="startWeekDay"
            options={weekDayOptions}
            value={profile?.startWeekDay || ""}
            label={t("agency.client.week.day")}
            margin="none"
            className={agencyClientStyles.fieldWidth}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!get(`startWeekDay`, errors)}
            classes={{
              root: css`
                ~ fieldset {
                  border-color: var(--light-grey);
                }
              `,
            }}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
          />
          <Select
            name="budgetPlanning"
            options={budgetPlanningOptions}
            value={profile?.budgetPlanning || ""}
            label={t("agency.client.budgetPlanning")}
            margin="none"
            className={agencyClientStyles.fieldWidth}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!get(`budgetPlanning`, errors)}
            classes={{
              root: css`
                ~ fieldset {
                  border-color: var(--light-grey);
                }
              `,
            }}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
          />
        </Box>
      </Box>
      <Box marginTop="var(--spacing-xxl-6)">
        <h5 className="BodyBody-1BlackBold">
          {t("agency.client.payment.terms.conditions")}
        </h5>
        <h5 className="BodyBody-1BlackRegular">{t(`agency.payment.info`)}</h5>
        <TextInput
          id="billingTerms"
          name="billingTerms"
          value={profile?.billingTerms}
          margin="normal"
          size="medium"
          className={agencyClientStyles.fieldWidthWider2}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!hasAuthority(["AUTH_EDIT_AGENCY_CLIENT_PROFILE"])}
          multiline
          rowsMax={13}
          error={!!get(`billingTerms`, errors)}
          errorMessage={get(`billingTerms`, errors)}
        />
      </Box>
    </Box>
  )
}
